import React, { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { useData } from '../DataProvider';
import { getBucketLabel, isMacPlatform } from '../bucketLabels';
import { storage } from '../../firebase';
import { ref, getDownloadURL } from 'firebase/storage';

/**
 * PdfReportGenerator component
 * Generates an HTML report that can be viewed in an overlay dialog and exported to PDF
 * - App information
 * - Clickable table of contents
 * - Violation summaries grouped by ACR ID with click images
 * - Event summaries (7 specific sections only)
 */
const PdfReportGenerator = ({ appInfo, viewConfig = {} }) => {
  const allowPdfDownload = !!viewConfig?.pdfDownload;
  const {
    autoinspectData,
    reviewedReport,
    mouseClickRowsWithImages,
    spans,
    eventSummaries,
    files,
    installerFileInfo,
    platform,
  } = useData();

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportHtml, setReportHtml] = useState(null);

  // Helper to format time
  const formatTime = (ms) => {
    if (typeof ms !== 'number' || isNaN(ms)) return '';
    const sign = ms < 0 ? '-' : '';
    const absMs = Math.abs(ms);
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((absMs % 1000) / 1000);
    const milliseconds = Math.floor(absMs % 1000);

    if (hours > 0) {
      return `${sign}${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
    } else {
      return `${sign}${minutes}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
    }
  };


  // Load image from Firebase storage and convert to base64
  const loadImageAsBase64 = async (imageFileName) => {
    if (!appInfo?.clickimagesPath || !imageFileName) {
      return null;
    }

    try {
      const firebaseImagePath = `${appInfo.clickimagesPath}/${imageFileName}.jpg`;
      const imageRef = ref(storage, firebaseImagePath);
      const url = await getDownloadURL(imageRef);

      // Fetch image and convert to base64
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  };

  // Helper to get video offset for a click image filename
  const getVideoOffsetForImage = (imageFileName) => {
    if (!mouseClickRowsWithImages || !Array.isArray(mouseClickRowsWithImages) || !imageFileName) {
      return null;
    }
    const image = mouseClickRowsWithImages.find((img) => img.clickImageFileName === imageFileName);
    return image?.VideoOffset ?? null;
  };

  // Generate HTML report and open in popup window
  const viewReport = async () => {
    if (!appInfo) {
      alert('App information not available');
      return;
    }

    setLoading(true);

    try {
      // Build spans array: sort by startActionMs (earliest first), then endActionMs (earliest first), with "all" last
      const allSpans = Array.isArray(spans) ? [...spans] : [];
      const allSpan = { scenario: 'all', startActionMs: null, endActionMs: null };
      
      // Separate "all" span from other spans
      const otherSpans = allSpans.filter(span => (span.scenario || 'unnamed') !== 'all');
      
      // Sort by startActionMs (treating null/undefined as Infinity), then by endActionMs
      const sortedSpans = otherSpans.sort((a, b) => {
        const aStart = a.startActionMs ?? Infinity;
        const bStart = b.startActionMs ?? Infinity;
        
        if (aStart !== bStart) {
          return aStart - bStart;
        }
        
        // If start times are equal, sort by end time
        const aEnd = a.endActionMs ?? Infinity;
        const bEnd = b.endActionMs ?? Infinity;
        return aEnd - bEnd;
      });
      
      // Add "all" span at the end
      const spansToUse = [...sortedSpans, allSpan];
      const spanNames = spansToUse.map(s => s.scenario || 'unnamed');

      // Find installer file
      let installerFile = null;
      if (files && Array.isArray(files) && files.length > 0 && appInfo.inspection) {
        const watchDir = appInfo.inspection;
        for (const file of files) {
          if (file.FileName) {
            const fileName = file.FileName.toLowerCase();
            if (watchDir.toLowerCase().endsWith(fileName.replace(/\\/g, '/').split('/').pop())) {
              installerFile = file;
              break;
            }
          }
        }
        if (!installerFile) {
          for (const file of files) {
            if (file.FileName) {
              const baseFileName = file.FileName.toLowerCase().replace(/\\/g, '/').split('/').pop();
              if (watchDir.toLowerCase().endsWith(baseFileName)) {
                installerFile = file;
                break;
              }
            }
          }
        }
      }
      
      // The watch-dir-name match above rarely holds for a .pkg / .dmg run (and never for an
      // autowatch run); the collector's own installerfileinfo.json is the authoritative record.
      if (!installerFile && installerFileInfo && typeof installerFileInfo === 'object') {
        installerFile = installerFileInfo;
      }

      // Process violations and load images
      // Build a map of reviewed violations by RuleID+Scenario for quick lookup
      const reviewedViolationsMap = new Map();
      if (reviewedReport?.results) {
        reviewedReport.results.forEach(v => {
          const key = `${v.RuleID}_${v.Scenario}`;
          reviewedViolationsMap.set(key, v);
        });
      }

      // Collect all violations to include in the report
      const violationsToProcess = [];
      
      // Start with all reviewed violations that are NOT ignored
      if (reviewedReport?.results) {
        reviewedReport.results.forEach(violation => {
          if (violation.ignore !== true) {
            violationsToProcess.push(violation);
          }
        });
      }
      
      // Process autoinspect violations (if they exist)
      if (autoinspectData?.summarizedInfo?.results) {
        for (const autoinspectViolation of autoinspectData.summarizedInfo.results) {
          const key = `${autoinspectViolation.RuleID}_${autoinspectViolation.Scenario}`;
          const reviewedVersion = reviewedViolationsMap.get(key);
          
          // If reviewed version exists and is ignored, skip this violation
          if (reviewedVersion && reviewedVersion.ignore === true) {
            continue;
          }
          
          // If reviewed version exists and is not ignored, we already added it above
          // If no reviewed version exists, use autoinspect version
          if (!reviewedVersion) {
            violationsToProcess.push(autoinspectViolation);
          }
        }
      }
      
      // Remove duplicates (in case a reviewed violation matches an autoinspect one)
      const uniqueViolations = [];
      const seenKeys = new Set();
      for (const violation of violationsToProcess) {
        const key = `${violation.RuleID}_${violation.Scenario}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueViolations.push(violation);
        }
      }

      // Load images for all violations
      const violationsWithImages = [];
      for (const violation of uniqueViolations) {
        // Use reviewed version if available, otherwise use autoinspect version
        const key = `${violation.RuleID}_${violation.Scenario}`;
        const reviewedVersion = reviewedViolationsMap.get(key);
        const violationToUse = reviewedVersion || violation;
        
        const violationData = {
          ...violationToUse,
          evidenceImages: []
        };

        const evidenceFileNames = violationToUse.EvidenceFileNames || [];
        if (evidenceFileNames.length > 0) {
          for (const evidenceFileName of evidenceFileNames) {
            let imageFileName = evidenceFileName;
            
            if (evidenceFileName.startsWith('Inspect/')) {
              imageFileName = evidenceFileName.replace(/\\/g, '/').split('/').pop();
              imageFileName = imageFileName.replace(/\.(jpg|png|jpeg)$/i, '');
              if (imageFileName.startsWith('watch_')) {
                imageFileName = imageFileName.substring(6);
              }
            } else {
              if (imageFileName.includes('/') || imageFileName.includes('\\')) {
                imageFileName = imageFileName.replace(/\\/g, '/').split('/').pop();
              }
              imageFileName = imageFileName.replace(/\.(jpg|png|jpeg)$/i, '');
            }

            if (imageFileName && imageFileName.trim() !== '') {
              const imageData = await loadImageAsBase64(imageFileName);
              const videoOffset = getVideoOffsetForImage(imageFileName);
              if (imageData) {
                violationData.evidenceImages.push({
                  data: imageData,
                  videoOffset: videoOffset
                });
              }
            }
          }
        }

        violationsWithImages.push(violationData);
      }

      // Generate HTML
      const html = generateReportHTML({
        appInfo,
        installerFile,
        installerFileInfoData: installerFileInfo || null,
        platform,
        violations: violationsWithImages,
        eventSummaries,
        spans: spansToUse,
        spanNames,
        formatTime
      });

      // Store HTML and open dialog
      setReportHtml(html);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setReportHtml(null);
  };

  return (
    <>
      {allowPdfDownload && (
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
          onClick={viewReport}
          disabled={loading || !appInfo}
          sx={{ py: 0.5, whiteSpace: 'nowrap', minHeight: 28 }}
        >
          {loading ? 'Loading…' : 'View Report'}
        </Button>
      )}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }}
      >
        <DialogTitle>
          Inspection Report - {appInfo?.appName || 'App'}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 70px)', overflow: 'hidden' }}>
          {reportHtml && (
            <iframe
              srcDoc={reportHtml}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title="Inspection Report"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Generate complete HTML report document
function generateReportHTML({ appInfo, installerFile, installerFileInfoData, platform, violations, eventSummaries, spans, spanNames, formatTime }) {
      // Section KEYS are the contract; headings come from the platform-aware label map, and on
      // macOS a section with nothing in it is left out (a "(0)" TOC of Windows-flavoured names
      // was the whole report for a mac run).
      const mac = isMacPlatform(platform);
      const sectionHeading = (key) => escapeHtml(getBucketLabel(key, platform));
      const allowedSections = [
        'TaskLifeCycles',
        'CertificateLifeCycles',
        'FirewallRuleLifeCycles',
        'StartupLifeCycles',
        'UninstallerLifeCycles',
        'DesktopItemLifeCycles',
        'ServiceLifeCycles',
        'RegistryASEPsWritten',
        'InterestingDirectoriesWritten',
        'BitsFilesInfo',
        'InterestingFileInfo'
      ];
      
  // Helper to escape HTML
  const escapeHtml = (text) => {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  };

  // Generate app info section
  let appInfoHTML = `
    <section id="app-info">
      <h1>App Information</h1>
      <p><strong>App Name:</strong> ${escapeHtml(appInfo.appName || 'N/A')}</p>
      <p><strong>Version:</strong> ${escapeHtml(appInfo.version || 'N/A')}</p>
      <p><strong>Inspection:</strong> ${escapeHtml(appInfo.inspection || 'N/A')}</p>
  `;

  if (installerFile) {
    appInfoHTML += '<h2>Installer File</h2>';
    if (installerFile.FileName) {
      appInfoHTML += `<p><strong>File Name:</strong> ${escapeHtml(installerFile.FileName)}</p>`;
    }
    if (installerFile.FileSize !== undefined) {
      appInfoHTML += `<p><strong>File Size:</strong> ${installerFile.FileSize} bytes</p>`;
    }
    if (installerFile.SHA256) {
      appInfoHTML += `<p><strong>SHA256:</strong> ${escapeHtml(installerFile.SHA256)}</p>`;
    }
    if (installerFile.FileType) {
      appInfoHTML += `<p><strong>File Type:</strong> ${escapeHtml(installerFile.FileType)}</p>`;
    }
    if (installerFile.ValidCertificate !== undefined) {
      appInfoHTML += `<p><strong>Valid Certificate:</strong> ${installerFile.ValidCertificate ? 'Yes' : 'No'}</p>`;
    }
    if (installerFile.CertificateSubject) {
      appInfoHTML += `<p><strong>Certificate Subject:</strong> ${escapeHtml(installerFile.CertificateSubject)}</p>`;
    }
    // The Pe* slots are platform-neutral in practice: for a macOS .app target aebuild fills
    // PeProductName / PeProductVersion with the bundle id / CFBundleShortVersionString; a
    // .pkg / .dmg carries only name / hash / size, so the labels must not claim "PE".
    if (installerFile.PeCompanyName) {
      appInfoHTML += `<p><strong>Company:</strong> ${escapeHtml(installerFile.PeCompanyName)}</p>`;
    }
    if (installerFile.PeProductName) {
      appInfoHTML += `<p><strong>Product:</strong> ${escapeHtml(installerFile.PeProductName)}</p>`;
    }
    const installerVersion = installerFile.PeFileVersion || installerFile.PeProductVersion;
    if (installerVersion) {
      appInfoHTML += `<p><strong>Version:</strong> ${escapeHtml(installerVersion)}</p>`;
    }
    if (installerFile.SHA1) {
      appInfoHTML += `<p><strong>SHA1:</strong> ${escapeHtml(installerFile.SHA1)}</p>`;
    }
  }

  if (appInfo.notes) {
    try {
      let notesText = appInfo.notes;
      if (notesText.startsWith('data:text/plain;base64,')) {
        notesText = atob(notesText.split(',')[1]);
      } else if (notesText.startsWith('data:text/plain,')) {
        notesText = decodeURIComponent(notesText.split(',')[1]);
      }
      appInfoHTML += `<h2>Notes</h2><p>${escapeHtml(notesText)}</p>`;
                } catch (e) {
      // Ignore errors
    }
  }

  appInfoHTML += '</section>';

  // Generate table of contents
  const violationCount = violations && Array.isArray(violations) ? violations.length : 0;
  let tocHTML = `
    <section id="toc">
      <h1>Table of Contents</h1>
      <ul>
        <li><a href="#app-info">App Information</a></li>
        <li><a href="#violations">Violation Summaries (${violationCount} ${violationCount === 1 ? 'item' : 'items'})</a></li>
        <li><a href="#event-summaries">Event Summaries</a></li>
  `;
  if (installerFileInfoData) {
    tocHTML += `<li><a href="#InstallerInfo">Installer Info</a></li>`;
  }
  for (const sectionName of allowedSections) {
    const sectionItems = eventSummaries && eventSummaries[sectionName] && Array.isArray(eventSummaries[sectionName]) ? eventSummaries[sectionName] : [];
    const itemCount = sectionItems.length;
    if (mac && itemCount === 0) continue;
    tocHTML += `<li><a href="#${sectionName}">${sectionHeading(sectionName)} (${itemCount})</a></li>`;
  }
  tocHTML += '</ul></section>';

  // Generate violations section
  let violationsHTML = '<section id="violations"><h1>Violation Summaries</h1>';
  if (violations && violations.length > 0) {
    for (const violation of violations) {
      violationsHTML += `<div class="violation"><h2>${escapeHtml(violation.RuleID || 'Unknown Rule')}</h2>`;
      if (violation.Scenario) {
        violationsHTML += `<p><strong>Scenario:</strong> ${escapeHtml(violation.Scenario)}</p>`;
      }
      if (violation.ViolationSummary) {
        violationsHTML += `<p><strong>Summary:</strong> ${escapeHtml(violation.ViolationSummary)}</p>`;
      }
      
      if (violation.evidenceImages && violation.evidenceImages.length > 0) {
        for (let idx = 0; idx < violation.evidenceImages.length; idx++) {
          const img = violation.evidenceImages[idx];
          if (idx % 3 === 0) {
            if (idx > 0) violationsHTML += '</div>';
            violationsHTML += '<div class="evidence-images">';
          }
          const offsetText = img.videoOffset != null ? formatTime(img.videoOffset) : 'Time unknown';
          violationsHTML += `
            <div class="evidence-image">
              <img src="${img.data}" alt="Evidence ${idx + 1}" style="max-height: 150px; object-fit: contain;" />
              <p class="image-time">${escapeHtml(offsetText)}</p>
            </div>
          `;
        }
        violationsHTML += '</div>';
      }
      violationsHTML += '</div>';
    }
      } else {
    violationsHTML += '<p>No violations found.</p>';
  }
  violationsHTML += '</section>';

  // Generate event summaries sections
  let eventSummariesHTML = '<section id="event-summaries"><h1>Event Summaries</h1>';
  if (installerFileInfoData) {
    eventSummariesHTML += '<section id="InstallerInfo" class="event-section"><h2>Installer Info</h2>';
    if (installerFileInfoData.SHA256) {
      const vtUrl = `https://www.virustotal.com/#/file/${installerFileInfoData.SHA256}/detection`;
      eventSummariesHTML += `<p><a href="${escapeHtml(vtUrl)}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none; font-size: 10pt;">VirusTotal</a></p>`;
    }
    const entries = Object.entries(installerFileInfoData).filter(([, v]) => v != null && v !== '');
    if (entries.length > 0) {
      eventSummariesHTML += '<table class="event-table"><thead><tr><th>Field</th><th>Value</th></tr></thead><tbody>';
      for (const [key, value] of entries) {
        const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : escapeHtml(String(value));
        eventSummariesHTML += `<tr><td style="font-weight:600">${escapeHtml(key)}</td><td style="word-break:break-all">${displayValue}</td></tr>`;
      }
      eventSummariesHTML += '</tbody></table>';
    } else {
      eventSummariesHTML += '<p>No installer info available.</p>';
    }
    eventSummariesHTML += '</section>';
  }
  for (const sectionName of allowedSections) {
    if (eventSummaries && eventSummaries[sectionName] && Array.isArray(eventSummaries[sectionName])) {
      const items = eventSummaries[sectionName];
      if (mac && items.length === 0) continue;
      eventSummariesHTML += `<section id="${sectionName}" class="event-section"><h2>${sectionHeading(sectionName)}</h2>`;
      
      if (items.length === 0) {
        eventSummariesHTML += '<p>No items occurred.</p>';
      } else {
        eventSummariesHTML += `<p>(${items.length} ${items.length === 1 ? 'item' : 'items'})</p>`;
        eventSummariesHTML += '<table class="event-table"><thead><tr><th>Item Name</th><th>Process</th>';
        for (const spanName of spanNames) {
          // Allow span names to wrap in headers
          eventSummariesHTML += `<th>${escapeHtml(spanName)}</th>`;
        }
        eventSummariesHTML += '</tr></thead><tbody>';

        // Sort items by ItemName
        const sortedItems = [...items].sort((a, b) => {
          const aName = (a.ItemName || '').toLowerCase();
          const bName = (b.ItemName || '').toLowerCase();
          return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
        });

        for (const item of sortedItems) {
          const itemName = item.ItemName || '';
          if (!itemName) continue;
          try {

            // Extract metadata from item (can be directly on item or in metadata property)
            const itemMetadata = item.metadata || {};
            if (item.FileInfo) itemMetadata.FileInfo = item.FileInfo;
            if (item.CertInfo) itemMetadata.CertInfo = item.CertInfo;
            if (item.ProcInfo) {
              itemMetadata.ProcInfo = item.ProcInfo;
              if (sectionName === 'ProcessLifeCycles' && item.ProcInfo.Details) {
                itemMetadata.Details = item.ProcInfo.Details;
              }
              if (sectionName === 'ProcessLifeCycles' && item.ProcInfo.Children && Array.isArray(item.ProcInfo.Children)) {
                itemMetadata.Children = item.ProcInfo.Children;
              }
            }
            // Extract BitsInfo from first process for BitsFilesInfo
            if (sectionName === 'BitsFilesInfo' && item.Processes && Array.isArray(item.Processes) && item.Processes.length > 0) {
              try {
                const firstProc = item.Processes[0];
                if (firstProc && firstProc.BitsInfo && typeof firstProc.BitsInfo === 'object' && !Array.isArray(firstProc.BitsInfo)) {
                  itemMetadata.BitsInfo = firstProc.BitsInfo;
                }
              } catch (e) {
                console.warn('Error extracting BitsInfo:', e);
                // Continue without BitsInfo if extraction fails
              }
            }

          // Handle grouped rows (multiple processes)
          if (item.Processes && Array.isArray(item.Processes) && item.Processes.length > 0) {
            let itemNameCell = escapeHtml(itemName);
            // Add VirusTotal link for InterestingFileInfo section
            if (sectionName === 'InterestingFileInfo' && itemMetadata.FileInfo?.SHA256) {
              const sha256 = itemMetadata.FileInfo.SHA256;
              const vtUrl = `https://www.virustotal.com/#/file/${sha256}/detection`;
              itemNameCell += `<br/><a href="${escapeHtml(vtUrl)}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none; font-size: 8pt;">VirusTotal</a>`;
            }
            if (itemMetadata.Details) {
              itemNameCell += ` - ${escapeHtml(itemMetadata.Details)}`;
            }
            if (itemMetadata.FileInfo?.FileType) {
              itemNameCell += ` [${escapeHtml(itemMetadata.FileInfo.FileType)}]`;
            }

            const rowspan = item.Processes.length;
            const hasMetadata = itemMetadata.FileInfo || itemMetadata.CertInfo || itemMetadata.BitsInfo || itemMetadata.ProcInfo || itemMetadata.Children;
            const totalRowspan = hasMetadata ? rowspan + 1 : rowspan;

            for (let procIdx = 0; procIdx < item.Processes.length; procIdx++) {
              const proc = item.Processes[procIdx];
              eventSummariesHTML += '<tr>';
              if (procIdx === 0) {
                eventSummariesHTML += `<td rowspan="${totalRowspan}" class="item-name-cell">${itemNameCell}</td>`;
              }
              eventSummariesHTML += `<td class="process-name">${escapeHtml(proc.ProcessName || 'unknown')}</td>`;
              
              for (const spanName of spanNames) {
                const cellValue = proc.Spans ? proc.Spans[spanName] : (proc.LifeCycle ? (typeof proc.LifeCycle === 'string' ? proc.LifeCycle : (proc.LifeCycle[spanName] || '')) : '');
                
                if (proc.Spans) {
                  // Boolean section
                  if (cellValue === true) {
                    eventSummariesHTML += '<td class="checkmark-cell"><span class="checkmark">✓</span></td>';
                      } else {
                    eventSummariesHTML += '<td class="checkmark-cell">—</td>';
                      }
                } else if (proc.LifeCycle) {
                  // Lifecycle section
                  const lifecycleStr = String(cellValue || '');
                  if (lifecycleStr) {
                    eventSummariesHTML += `<td class="lifecycle-cell">${renderLifecycleChips(lifecycleStr)}</td>`;
                    } else {
                    eventSummariesHTML += '<td></td>';
                  }
                } else {
                  eventSummariesHTML += '<td></td>';
                }
              }
              
              eventSummariesHTML += '</tr>';
            }

            // Add metadata row if present
            if (hasMetadata) {
              try {
                const metadataColspan = 1 + spanNames.length; // Don't include item name column (it's rowspan)
                eventSummariesHTML += `<tr class="metadata-row"><td colspan="${metadataColspan}" class="metadata-cell">`;
                eventSummariesHTML += renderMetadata(itemMetadata);
                eventSummariesHTML += '</td></tr>';
              } catch (e) {
                console.error('Error rendering metadata for item:', itemName, e);
                // Skip metadata row if rendering fails
              }
            }
          } else {
            // Single process row (backward compatibility)
            const hasMetadata = itemMetadata.FileInfo || itemMetadata.CertInfo || itemMetadata.BitsInfo || itemMetadata.ProcInfo || itemMetadata.Children;
            
            let singleItemNameCell = escapeHtml(itemName);
            // Add VirusTotal link for InterestingFileInfo section
            if (sectionName === 'InterestingFileInfo' && itemMetadata.FileInfo?.SHA256) {
              const sha256 = itemMetadata.FileInfo.SHA256;
              const vtUrl = `https://www.virustotal.com/#/file/${sha256}/detection`;
              singleItemNameCell += `<br/><a href="${escapeHtml(vtUrl)}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none; font-size: 8pt;">VirusTotal</a>`;
            }
            
            eventSummariesHTML += '<tr>';
            eventSummariesHTML += `<td class="item-name-cell">${singleItemNameCell}</td>`;
            eventSummariesHTML += `<td class="process-name">${escapeHtml(item.processName || 'unknown')}</td>`;
            
            for (const spanName of spanNames) {
              const cellValue = item.spanData ? item.spanData[spanName] : '';
              if (item.dataType === 'boolean') {
                if (cellValue === true) {
                  eventSummariesHTML += '<td class="checkmark-cell"><span class="checkmark">✓</span></td>';
                    } else {
                  eventSummariesHTML += '<td class="checkmark-cell">—</td>';
                }
                    } else {
                const lifecycleStr = String(cellValue || '');
                if (lifecycleStr) {
                  eventSummariesHTML += `<td class="lifecycle-cell">${renderLifecycleChips(lifecycleStr)}</td>`;
                } else {
                  eventSummariesHTML += '<td></td>';
                }
              }
            }
            
            eventSummariesHTML += '</tr>';

            // Add metadata row if present
            if (hasMetadata) {
              try {
                const metadataColspan = 2 + spanNames.length;
                eventSummariesHTML += `<tr class="metadata-row"><td colspan="${metadataColspan}" class="metadata-cell">`;
                eventSummariesHTML += renderMetadata(itemMetadata);
                eventSummariesHTML += '</td></tr>';
              } catch (e) {
                console.error('Error rendering metadata for item:', itemName, e);
                // Skip metadata row if rendering fails
              }
            }
          }
          } catch (e) {
            console.error('Error processing item:', itemName || 'unknown', e);
            // Continue with next item if this one fails
          }
        }

        eventSummariesHTML += '</tbody></table>';
      }
      eventSummariesHTML += '</section>';
    }
  }
  eventSummariesHTML += '</section>';

  // Render lifecycle chips as HTML
  function renderLifecycleChips(lifecycleStr) {
    return lifecycleStr.split('').map((char, i) => {
      const code = char || '.';
      const isUpper = code === code.toUpperCase();
      let chipClass = 'lifecycle-chip';
      let chipContent = escapeHtml(code);

      if (code === 'B' || code === 'b') {
        chipClass += isUpper ? ' lifecycle-chip-b-filled' : ' lifecycle-chip-b-outline';
      } else if (code === 'E' || code === 'e') {
        chipClass += isUpper ? ' lifecycle-chip-e-filled' : ' lifecycle-chip-e-outline';
                        } else {
        chipClass += ' lifecycle-chip-other';
      }

      return `<span class="${chipClass}">${chipContent}</span>`;
    }).join('');
  }

  // Render metadata (FileInfo, CertInfo, ProcInfo, Children)
  function renderMetadata(metadata) {
    try {
      if (!metadata || typeof metadata !== 'object') {
        return '<div class="metadata-content"></div>';
      }
      let html = '<div class="metadata-content">';
      
      try {
        if (metadata.FileInfo) {
          html += '<div class="metadata-section"><strong>File Info:</strong><ul>';
          for (const [key, value] of Object.entries(metadata.FileInfo)) {
            try {
              const valueStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
              html += `<li><strong>${escapeHtml(key)}:</strong> ${escapeHtml(valueStr)}</li>`;
            } catch (e) {
              html += `<li><strong>${escapeHtml(key)}:</strong> [Error]</li>`;
            }
          }
          html += '</ul></div>';
        }
      } catch (e) {
        console.warn('Error rendering FileInfo:', e);
      }

      try {
        if (metadata.CertInfo) {
          html += '<div class="metadata-section"><strong>Certificate Info:</strong><ul>';
          for (const [key, value] of Object.entries(metadata.CertInfo)) {
            try {
              const valueStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
              html += `<li><strong>${escapeHtml(key)}:</strong> ${escapeHtml(valueStr)}</li>`;
            } catch (e) {
              html += `<li><strong>${escapeHtml(key)}:</strong> [Error]</li>`;
            }
          }
          html += '</ul></div>';
        }
      } catch (e) {
        console.warn('Error rendering CertInfo:', e);
      }

      try {
        if (metadata.BitsInfo && typeof metadata.BitsInfo === 'object' && !Array.isArray(metadata.BitsInfo)) {
          html += '<div class="metadata-section"><strong>Bits Info:</strong><ul>';
          for (const [key, value] of Object.entries(metadata.BitsInfo)) {
            try {
              let valueStr;
              // Format dates nicely
              if (value instanceof Date) {
                valueStr = value.toISOString();
              } else if (key === 'StartTime' || key === 'EndTime' || key === 'FileTime') {
                try {
                  if (value == null) {
                    valueStr = '';
                  } else {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                      valueStr = date.toISOString();
                    } else {
                      valueStr = String(value);
                    }
                  }
                } catch (e) {
                  valueStr = String(value || '');
                }
              } else {
                try {
                  valueStr = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value || '');
                } catch (e) {
                  // Handle circular references or other JSON.stringify errors
                  valueStr = '[Object]';
                }
              }
              html += `<li><strong>${escapeHtml(key)}:</strong> ${escapeHtml(valueStr)}</li>`;
            } catch (e) {
              html += `<li><strong>${escapeHtml(key)}:</strong> [Error]</li>`;
            }
          }
          html += '</ul></div>';
        }
      } catch (e) {
        console.warn('Error rendering BitsInfo:', e);
      }

      try {
        if (metadata.ProcInfo) {
          html += '<div class="metadata-section"><strong>Process Info:</strong><ul>';
          for (const [key, value] of Object.entries(metadata.ProcInfo)) {
            try {
              if (key === 'Actions' && Array.isArray(value)) {
                html += '<li><strong>Actions:</strong><ul>';
                for (let idx = 0; idx < value.length; idx++) {
                  const action = value[idx];
                  try {
                    const actionStr = JSON.stringify(action);
                    html += `<li>[${idx}]: ${escapeHtml(actionStr)}</li>`;
                  } catch (e) {
                    html += `<li>[${idx}]: [Error]</li>`;
                  }
                }
                html += '</ul></li>';
              } else {
                const valueStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
                html += `<li><strong>${escapeHtml(key)}:</strong> ${escapeHtml(valueStr)}</li>`;
              }
            } catch (e) {
              html += `<li><strong>${escapeHtml(key)}:</strong> [Error]</li>`;
            }
          }
          html += '</ul></div>';
        }
      } catch (e) {
        console.warn('Error rendering ProcInfo:', e);
      }

      try {
        if (metadata.Children && Array.isArray(metadata.Children)) {
          html += '<div class="metadata-section"><strong>Children:</strong><ul>';
          metadata.Children.forEach(child => {
            try {
              const childName = child.name || '';
              const childDetails = child.details || '';
              html += `<li>└─ ${escapeHtml(childName)}${childDetails ? ` - ${escapeHtml(childDetails)}` : ''}</li>`;
            } catch (e) {
              html += `<li>└─ [Error]</li>`;
            }
          });
          html += '</ul></div>';
        }
      } catch (e) {
        console.warn('Error rendering Children:', e);
      }

      html += '</div>';
      return html;
    } catch (e) {
      console.error('Error in renderMetadata:', e);
      return '<div class="metadata-content"><p>Error rendering metadata</p></div>';
    }
  }

  // Complete HTML document
  // Note: PDFs use light theme colors as they are static documents
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Inspection Report - ${escapeHtml(appInfo.appName || 'App')}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,400;0,700;1,400&family=Noto+Sans+SC:wght@400;700&family=Noto+Sans+TC:wght@400;700&family=Noto+Sans+JP:wght@400;700&family=Noto+Sans+KR:wght@400;700&family=Noto+Sans+Arabic:wght@400;700&family=Noto+Sans+Hebrew:wght@400;700&display=swap" rel="stylesheet">
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: 'Noto Sans', 'Noto Sans SC', 'Noto Sans TC', 'Noto Sans JP', 'Noto Sans KR', 'Noto Sans Arabic', 'Noto Sans Hebrew', sans-serif;
      font-size: 10pt;
      line-height: 1.3;
      color: #000;
      background: #fff;
      padding: 20px;
      max-width: 100%;
      margin: 0;
      width: 100%;
    }

    .report-header {
      position: static;
      background: white;
      padding: 10px 0;
      border-bottom: 2px solid #1976d2;
      margin-bottom: 20px;
    }

    .report-header h1 {
      font-size: 18pt;
      margin-bottom: 10px;
    }

    .report-actions {
      display: flex;
      gap: 10px;
    }

    .report-actions button {
      padding: 8px 16px;
      font-size: 12pt;
      background: #1976d2;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-family: inherit;
    }

    .report-actions button:hover {
      background: #1565c0;
    }

    .report-actions button:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    section {
      margin-bottom: 30px;
      page-break-inside: avoid;
    }

    h1 {
      font-size: 13pt;
      font-weight: bold;
      margin: 20px 0 15px 0;
      page-break-after: avoid;
    }

    h2 {
      font-size: 11pt;
      font-weight: bold;
      margin: 15px 0 10px 0;
      page-break-after: avoid;
    }

    p {
      margin: 5px 0;
    }

    #toc ul {
      list-style: none;
      padding-left: 0;
    }

    #toc li {
      margin: 5px 0;
    }

    #toc a {
      color: #1976d2;
      text-decoration: none;
    }

    #toc a:hover {
      text-decoration: underline;
    }

    .violation {
      margin: 20px 0;
    }

    .evidence-images {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      margin: 15px 0;
      min-height: 180px;
      //overflow: visible;
      page-break-inside: avoid;
      background: #f9f9f9;
    }

    .evidence-image {
      flex: 0 0 calc(33.333% - 4px);
      text-align: center;
      page-break-inside: avoid;
      max-height: 180px;
    }

    .evidence-image img {
      max-width: 100%;
      max-height: 150px;
      height: auto;
      border: 1px solid #ddd;
      display: block;
      object-fit: contain;
      margin: 0 auto;
    }

    .image-time {
      font-size: 7pt;
      margin-top: 5px;
      color: #666;
    }

    .event-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
      font-size: 7pt;
      table-layout: fixed;
    }

    .event-table th {
      background: #f5f5f5;
      padding: 4px 2px;
      text-align: left;
      font-weight: bold;
      border-bottom: 2px solid #ddd;
      font-size: 6.5pt;
    }

    .event-table th:nth-child(1) {
      width: 22%;
    }

    .event-table th:nth-child(2) {
      width: 18%;
    }

    .event-table th:nth-child(n+3) {
      text-align: center;
      width: auto;
      min-width: 30px;
      word-wrap: break-word;
      white-space: normal;
    }

    .event-table td {
      padding: 4px 2px;
      border-bottom: 1px solid #eee;
      vertical-align: top;
      font-size: 7pt;
    }

    .event-table td:nth-child(n+3) {
      word-wrap: break-word;
      white-space: normal;
      overflow-wrap: break-word;
    }

    .event-table .item-name-cell {
      max-width: 22%;
      word-wrap: break-word;
      font-size: 6.5pt;
    }

    .event-table .process-name {
      font-family: monospace;
      font-size: 6.5pt;
      max-width: 18%;
      word-wrap: break-word;
    }

    .metadata-row {
      background: #f9f9f9;
    }

    .metadata-cell {
      padding: 10px !important;
      font-size: 7pt;
    }

    .metadata-content {
      padding-left: 10px;
    }

    .metadata-section {
      margin: 8px 0;
    }

    .metadata-section ul {
      margin: 4px 0;
      padding-left: 20px;
    }

    .metadata-section li {
      margin: 2px 0;
      font-family: monospace;
      font-size: 6.5pt;
    }

    .checkmark-cell {
      text-align: center;
    }

    .checkmark {
      display: inline-block;
      width: 12px;
      height: 12px;
      line-height: 12px;
      border-radius: 50%;
      background: #1976d2;
      color: white;
      font-weight: bold;
      font-size: 9px;
    }

    .lifecycle-cell {
      text-align: center;
    }

    .lifecycle-chip {
      display: inline-block;
      width: 14px;
      height: 14px;
      line-height: 14px;
      text-align: center;
      font-weight: bold;
      font-size: 6pt;
      margin: 0 0.5px;
      border-radius: 2px;
    }

    .lifecycle-chip-b-filled {
      background: #1976d2;
      color: white;
    }

    .lifecycle-chip-b-outline {
      border: 1px solid #1976d2;
      color: #1976d2;
    }

    .lifecycle-chip-e-filled {
      background: #9c27b0;
      color: white;
    }

    .lifecycle-chip-e-outline {
      border: 1px solid #9c27b0;
      color: #9c27b0;
    }

    .lifecycle-chip-other {
      border: 1px solid #bdbdbd;
      color: #757575;
    }

    @media print {
      body {
        padding: 0;
      }


      .report-header {
        position: static;
        page-break-after: avoid;
      }
      
      .report-actions {
        display: none !important;
      }

      section {
        page-break-inside: avoid;
      }

      h1, h2 {
        page-break-after: avoid;
      }

      .violation {
        page-break-inside: avoid;
      }

      .evidence-images {
        page-break-inside: avoid;
      }
    }
    
    /* Additional CSS for html2pdf rendering */
    body {
      overflow: visible !important;
      min-height: 100vh;
      position: relative;
    }
    
    #report-container {
      width: 100%;
      max-width: none;
      min-height: 100vh;
      position: relative;
      background: white;
    }
    
    .report-header {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    /* Hide buttons when generating PDF */
    body.generating-pdf .report-actions {
      display: none !important;
    }
    
    /* PDF Generation Progress Overlay */
    #pdf-progress-overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      z-index: 10000;
      justify-content: center;
      align-items: center;
    }
    
    #pdf-progress-overlay.active {
      display: flex;
    }
    
    .pdf-progress-content {
      background: white;
      padding: 30px 40px;
      border-radius: 8px;
      text-align: center;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    
    .pdf-progress-spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #1976d2;
      border-radius: 50%;
      width: 50px;
      height: 50px;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .pdf-progress-text {
      font-size: 16px;
      color: #333;
      font-weight: 500;
    }

    @page {
      size: letter landscape;
      margin: 10mm;
    }
  </style>
</head>
<body>
  <div id="report-container" style="width: 100%; min-height: 100vh;">
    <div class="report-header">
      <h1>Inspection Report - ${escapeHtml(appInfo.appName || 'App')}</h1>
      <div class="report-actions">
        <button id="generatePdfBtn" onclick="generatePDF()">Generate PDF</button>
        <button onclick="window.print()">Print</button>
      </div>
    </div>

    ${appInfoHTML}
    ${tocHTML}
    ${violationsHTML}
    ${eventSummariesHTML}
  </div>
  
  <!-- PDF Generation Progress Overlay -->
  <div id="pdf-progress-overlay">
    <div class="pdf-progress-content">
      <div class="pdf-progress-spinner"></div>
      <div class="pdf-progress-text" id="pdf-progress-text">Generating PDF...</div>
    </div>
  </div>

  <script>
    // Wait for DOM to be fully ready
    function waitForDOMReady() {
      return new Promise((resolve) => {
        if (document.readyState === 'complete') {
          resolve();
        } else {
          window.addEventListener('load', resolve, { once: true });
        }
      });
    }

    // Wait for all images to load before PDF generation
    function waitForImagesToLoad(timeoutMs = 10000) {
      return new Promise((resolve) => {
        const images = Array.from(document.querySelectorAll('img'));
        if (images.length === 0) {
          resolve();
          return;
        }

        let loadedCount = 0;
        let errorCount = 0;
        const totalImages = images.length;

        // Check if image is already loaded and rendered
        function isImageLoaded(img) {
          // For base64 images, they should be complete and have natural dimensions
          // naturalHeight > 0 means the image has been decoded and rendered
          return img.complete && img.naturalHeight > 0;
        }

        // Count already loaded images
        images.forEach(img => {
          if (isImageLoaded(img)) {
            loadedCount++;
          }
        });

        // If all images are already loaded, resolve immediately with a small delay
        // to ensure rendering is complete
        if (loadedCount === totalImages) {
          setTimeout(() => resolve(), 100);
          return;
        }

        // Set up timeout to prevent infinite waiting
        const timeoutId = setTimeout(() => {
          console.warn('Image loading timeout: ' + loadedCount + '/' + totalImages + ' images loaded');
          // Resolve anyway to prevent infinite waiting
          resolve();
        }, timeoutMs);

        // Set up load/error handlers for images that aren't loaded yet
        images.forEach(img => {
          if (isImageLoaded(img)) {
            // Already loaded, skip
            return;
          }

          const onLoad = () => {
            loadedCount++;
            if (loadedCount + errorCount === totalImages) {
              clearTimeout(timeoutId);
              resolve();
            }
          };

          const onError = () => {
            errorCount++;
            // Remove listeners to prevent memory leaks
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
            if (loadedCount + errorCount === totalImages) {
              clearTimeout(timeoutId);
              resolve();
            }
          };

          // Add event listeners
          img.addEventListener('load', onLoad, { once: true });
          img.addEventListener('error', onError, { once: true });

          // For base64 images that are marked complete but not rendered,
          // try to force a reload check by temporarily changing src
          if (img.complete && img.naturalHeight === 0 && img.src.startsWith('data:')) {
            // Base64 images should load immediately, but give it a moment
            setTimeout(() => {
              if (isImageLoaded(img)) {
                onLoad();
              } else {
                // Image might be invalid, count as error
                onError();
              }
            }, 50);
          }
        });
      });
    }

    function generatePDF() {
      const btn = document.getElementById('generatePdfBtn');
      const progressOverlay = document.getElementById('pdf-progress-overlay');
      const progressText = document.getElementById('pdf-progress-text');
      
      btn.disabled = true;
      btn.textContent = 'Generating PDF...';
      
      // Show progress overlay
      progressOverlay.classList.add('active');
      progressText.textContent = 'Preparing PDF...';
      
      // Add class to body to hide buttons during PDF generation
      document.body.classList.add('generating-pdf');

      // First ensure DOM is ready
      waitForDOMReady().then(() => {
        // Wait for fonts to load to prevent layout shifts and blank pages
        const fontPromise = document.fonts && document.fonts.ready 
          ? document.fonts.ready 
          : Promise.resolve();

        return fontPromise;
      }).then(() => {
        // Small delay to ensure rendering is complete
        return new Promise(resolve => setTimeout(resolve, 100));
      }).then(() => {
        // Wait for all images to load
        progressText.textContent = 'Loading images...';
        return waitForImagesToLoad(10000);
      }).then(() => {
        // Additional delay to ensure all rendering is complete
        progressText.textContent = 'Generating PDF...';
        return new Promise(resolve => setTimeout(resolve, 500));
      }).then(() => {
        generatePDFInternal();
      }).catch((error) => {
        console.error('Error during PDF preparation:', error);
        progressOverlay.classList.remove('active');
        document.body.classList.remove('generating-pdf');
        btn.disabled = false;
        btn.textContent = 'Generate PDF';
        alert('Failed to prepare PDF: ' + error.message);
      });
      
      function generatePDFInternal() {
        // Use the report container instead of body for more reliable capture
        const element = document.getElementById('report-container') || document.body;
        
        // Verify element exists and has content
        if (!element) {
          console.error('Report container not found');
          btn.disabled = false;
          btn.textContent = 'Generate PDF';
          alert('Failed to find report content');
          return;
        }

        // Verify element has dimensions and content
        const hasDimensions = element.offsetWidth > 0 && element.offsetHeight > 0;
        const hasContent = element.textContent && element.textContent.trim().length > 0;
        const imageCount = document.querySelectorAll('img').length;

        console.log('PDF generation - Element check:', {
          exists: !!element,
          hasDimensions: hasDimensions,
          width: element.offsetWidth,
          height: element.offsetHeight,
          scrollWidth: element.scrollWidth,
          scrollHeight: element.scrollHeight,
          hasContent: hasContent,
          contentLength: element.textContent ? element.textContent.length : 0,
          images: imageCount
        });

        if (!hasDimensions && element.scrollHeight === 0) {
          console.error('Element has no dimensions - cannot generate PDF');
          btn.disabled = false;
          btn.textContent = 'Generate PDF';
          alert('Report content has no dimensions. Please try again.');
          return;
        }

        const appName = ${JSON.stringify(appInfo.appName || 'app')};
        const version = ${JSON.stringify(appInfo.version || 'unknown')};
        const inspection = ${JSON.stringify(appInfo.inspection || 'inspection')};
        const filename = appName + '_v' + version + '_' + inspection + '_report.pdf';
        
        // For landscape letter: 11" width - 0.4" margins = 10.6" content width
        // At 72 DPI (PDF points), that's 763 points, which html2canvas should match
        const contentWidthInches = 14 - 0.4;
        const captureWidth = Math.round(contentWidthInches * 72); // ~763px to match PDF dimensions
        
        const opt = {
          margin: [0.8, 0.4, 0.8, 0.4],
          filename: filename,
          image: { type: 'jpeg', quality: 0.95 },
          pagebreak: { mode: 'css' },
          html2canvas: { 
            scale: 1,
            useCORS: true,
            allowTaint: false,
            logging: true,
            letterRendering: false,
            windowWidth: captureWidth,
            windowHeight: element.scrollHeight || element.offsetHeight || window.innerHeight,
            width: captureWidth,
            height: element.scrollHeight || element.offsetHeight,
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            backgroundColor: '#ffffff',
            onclone: function(clonedDoc) {
              // Verify images are loaded in the cloned document
              const clonedImages = clonedDoc.querySelectorAll('img');
              let loadedCount = 0;
              let totalCount = clonedImages.length;
              clonedImages.forEach(img => {
                if (img.complete && img.naturalHeight > 0) {
                  loadedCount++;
                } else {
                  console.warn('Image not fully loaded in cloned document:', img.src.substring(0, 50));
                }
              });
              console.log('Cloned document - Images loaded: ' + loadedCount + '/' + totalCount);
              
              // Ensure all images in cloned doc are visible
              clonedImages.forEach(img => {
                img.style.display = '';
                img.style.visibility = 'visible';
              });
              
              // Hide the report-actions buttons from the PDF
              const reportActions = clonedDoc.querySelector('.report-actions');
              if (reportActions) {
                reportActions.style.display = 'none';
              }
              
              // Apply print-style CSS to cloned document for full-width layout
              const clonedBody = clonedDoc.body;
              if (clonedBody) {
                clonedBody.style.visibility = 'visible';
                clonedBody.style.display = 'block';
                clonedBody.style.width = captureWidth + 'px';
                clonedBody.style.maxWidth = 'none';
                clonedBody.style.padding = '0';
                clonedBody.style.margin = '0';
              }
              const clonedContainer = clonedDoc.getElementById('report-container');
              if (clonedContainer) {
                clonedContainer.style.visibility = 'visible';
                clonedContainer.style.display = 'block';
                clonedContainer.style.width = '100%';
                clonedContainer.style.maxWidth = 'none';
              }
            }
          },
          jsPDF: { unit: 'in', format: [8.5, 11], orientation: 'landscape', compress: true }
        };

        html2pdf().set(opt).from(element).toPdf().get('pdf').then(function(pdf) {
          const totalPages = pdf.internal.getNumberOfPages();
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          
          // Generate timestamp for report
          const now = new Date();
          const reportDate = now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          const reportTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
          const timestamp = reportDate + ' ' + reportTime;
          
          // Add header and footer to each page
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            // Header - App name and version (left)
            pdf.setFontSize(10);
            pdf.setTextColor(60, 60, 60);
            pdf.text(appName + ' v' + version, 0.2, 0.4);
            
            // Header - Report generation timestamp (right)
            pdf.setFontSize(9);
            pdf.setTextColor(80, 80, 80);
            const timestampWidth = pdf.getTextWidth(timestamp);
            pdf.text(timestamp, pageWidth - timestampWidth - 0.2, 0.4);
            
            // Footer - Page number
            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            const pageText = 'Page ' + i + ' of ' + totalPages;
            const textWidth = pdf.getTextWidth(pageText);
            pdf.text(pageText, pageWidth - textWidth - 0.2, pageHeight - 0.3);
            
            // Optional: Add inspection info to footer left
            pdf.text(inspection, 0.2, pageHeight - 0.3);
          }
          
          return pdf;
        }).save().then(() => {
          console.log('PDF generated successfully');
          progressOverlay.classList.remove('active');
          document.body.classList.remove('generating-pdf');
          btn.disabled = false;
          btn.textContent = 'Generate PDF';
        }).catch((error) => {
          console.error('PDF generation error:', error);
          alert('Failed to generate PDF: ' + error.message);
          progressOverlay.classList.remove('active');
          document.body.classList.remove('generating-pdf');
          btn.disabled = false;
          btn.textContent = 'Generate PDF';
        });
      }
    }
  </script>
</body>
</html>`;

  return html;
}

export default PdfReportGenerator;
